import { useState } from 'react';

const faqs = [
  {
    question: 'What size businesses do you typically work with?',
    answer: 'We specialize in small to mid-sized businesses with annual revenues between $5 million and $75 million. Our consultants are experienced in navigating the unique challenges that come with businesses at this stage — from operational scaling to leadership transitions and exit readiness.',
  },
  {
    question: 'How long does a typical engagement last?',
    answer: 'Engagement length varies based on scope and objectives. A focused turnaround or profitability project typically runs 3–6 months, while a full strategic growth or exit planning engagement can span 6–18 months. We tailor every engagement to your specific timeline and goals.',
  },
  {
    question: 'Do you work with businesses outside of Texas?',
    answer: 'While our roots and primary focus are in Texas, we do work with clients across the United States. Our team is experienced in remote and hybrid engagement models, so geography is rarely a barrier to getting started.',
  },
  {
    question: 'What industries do you have experience in?',
    answer: 'Our consultants have deep experience across manufacturing, distribution, professional services, healthcare, technology, and retail. We bring cross-industry insight that often surfaces solutions others miss.',
  },
  {
    question: 'How do you charge for your services?',
    answer: 'We offer flexible engagement structures including project-based fees, monthly retainers, and performance-aligned arrangements. Pricing is discussed transparently during the discovery call once we understand the scope and complexity of your needs.',
  },
  {
    question: 'What makes TCSA different from other consulting firms?',
    answer: 'Unlike large generalist firms, we bring C-suite operators — not just advisors — to every engagement. Our team has sat in the seats your leadership team occupies, which means our recommendations are grounded in real-world execution, not just theory.',
  },
  {
    question: 'How do I know if my business is ready for a strategic sale?',
    answer: 'Readiness depends on several factors: financial performance, operational documentation, management depth, and market timing. We offer a comprehensive exit readiness assessment that gives you a clear picture of where you stand and what steps will maximize your valuation.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-texas-red text-xs font-bold tracking-widest uppercase mb-3 block">Got Questions?</span>
          <h2 className="text-3xl md:text-4xl font-bold text-texas-navy mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Everything you need to know before reaching out. Can't find your answer?{' '}
            <a href="#contact" className="text-texas-red font-medium hover:underline cursor-pointer">Send us a message.</a>
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div key={idx} className={`rounded-lg border transition-colors duration-200 overflow-hidden ${isOpen ? 'border-texas-red/30 bg-texas-red-light' : 'border-gray-200 bg-white'}`}>
                <button
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span className={`text-sm font-semibold leading-snug transition-colors ${isOpen ? 'text-texas-red' : 'text-texas-navy'}`}>
                    {faq.question}
                  </span>
                  <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-colors duration-200 ${isOpen ? 'bg-texas-red text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                  </div>
                </button>
                <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="px-6 pb-5 text-sm text-gray-500 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center bg-texas-navy rounded-lg px-8 py-8">
          <p className="text-white font-semibold text-base mb-1">Still have questions?</p>
          <p className="text-white/55 text-sm mb-5">Our team is happy to walk you through anything before you commit to a call.</p>
          <a href="#contact" className="inline-block bg-texas-red hover:bg-texas-red-dark text-white text-sm font-semibold px-8 py-3 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap">
            Get in Touch
          </a>
        </div>
      </div>
    </section>
  );
}
